import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { hatPlugin, istNativeApp } from './nativePlatform';
import { appFlags } from './appFlags';

/**
 * Datei-Ausgabe für Browser UND Android-App (docs/android-app-plan.md, P2).
 *
 * Browser: gewöhnlicher Download über einen unsichtbaren Link (wie file-saver).
 *
 * App: In der Android-WebView kommen Blob-Downloads nicht an — der Klick auf
 * `<a download href="blob:…">` verpufft ohne Meldung. Deshalb wird die Datei
 * in den App-Cache geschrieben (`Filesystem`, kein Speicherrecht nötig) und
 * der Teilen-Dialog geöffnet (`Share`). Darin kann der Nutzer speichern
 * (Dateien/Drive), per Mail verschicken oder in einer PDF-App öffnen.
 *
 * Die Aufrufer behalten im Browser ihre ORIGINAL-Anweisung (`doc.save`,
 * `saveAs`, …) und rufen diesen Helfer nur im App-Zweig — der Browserweg
 * bleibt damit Zeichen für Zeichen, wie er war.
 */

/** Unterordner im App-Cache. Android räumt den Cache bei Platzmangel selbst. */
const AUSGABE_ORDNER = 'ausgabe';

/**
 * Datei ausgeben. Browser: Download (der Klick passiert synchron im Aufruf,
 * die Klick-Geste des Nutzers bleibt also erhalten). App: Cache + Teilen-Dialog.
 *
 * Bricht der Nutzer den Teilen-Dialog ab, ist das KEIN Fehler (erfüllt sich
 * normal). Echte Fehler (Schreiben, Plugin fehlt, Teilen läuft schon) werden
 * geworfen — der Aufrufer meldet sie sichtbar, z. B. mit `ausgabeFehlerMelden`.
 */
export async function dateiAusgeben(daten: Blob, dateiname: string): Promise<void> {
  if (!istNativeApp()) {
    webDownload(daten, dateiname);
    return;
  }
  const uri = await dateiInCacheSchreiben(daten, dateiname);
  await dateiTeilen(uri, dateiname);
}

/**
 * NUR App: Datei in den App-Cache schreiben (Unterordner `ausgabe`, Name
 * bereinigt) und die `file://`-Uri zurückgeben — die Uri, die das
 * Share-Plugin und `AppFlags.emailEntwurf` erwarten (FileProvider,
 * `cache-path` in file_paths.xml). Im Browser gibt es keinen App-Cache:
 * dort lehnt die Funktion ab, statt still in IndexedDB zu schreiben.
 */
export async function dateiInCacheSchreiben(daten: Blob, dateiname: string): Promise<string> {
  if (!istNativeApp()) throw new Error('dateiInCacheSchreiben gibt es nur in der App.');
  const base64 = await blobZuBase64(daten);
  const { uri } = await Filesystem.writeFile({
    path: `${AUSGABE_ORDNER}/${cacheDateiname(dateiname)}`,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });
  return uri;
}

/**
 * NUR App: eine bereits im Cache liegende Datei über den Teilen-Dialog
 * ausgeben. Abbruch durch den Nutzer ist kein Fehler; echte Fehler werden
 * geworfen.
 */
export async function dateiTeilen(uri: string, dateiname: string): Promise<void> {
  try {
    await Share.share({ title: cacheDateiname(dateiname), files: [uri], dialogTitle: 'Datei speichern oder teilen' });
  } catch (e) {
    if (istNutzerAbbruch(e)) return;
    throw e;
  }
}

/** Empfänger, Betreff und Text eines Mail-Entwurfs — dieselben Werte wie im mailto-Weg. */
export interface MailEntwurf {
  an: string[];
  cc?: string[];
  betreff: string;
  text: string;
}

/**
 * Wie die App den Mail-Entwurf übergeben hat:
 * - `entwurf`: EIN Entwurf mit Empfängern, Betreff, Text und PDF (AppFlags.emailEntwurf)
 * - `abgebrochen`: der Nutzer hat die Auswahl der Mail-App geschlossen
 * - `rueckfall`: altes APK ohne `emailEntwurf` bzw. Fehler — erst Teilen, dann mailto
 */
export type MailUebergabe = 'entwurf' | 'abgebrochen' | 'rueckfall';

/**
 * NUR App (Vertrag E19): PDF und Mail als EINEN Entwurf an die Mail-App geben
 * (`AppFlags.emailEntwurf`, Android ACTION_SEND mit EXTRA_STREAM) — statt
 * Teilen-Dialog UND mailto, die zwei getrennte Entwürfe erzeugten.
 *
 * Rückfall, wenn das Plugin oder die Methode fehlt (ältere APK) oder der
 * Aufruf scheitert: ERST den Teilen-Dialog abwarten, DANN `mailtoOeffnen`.
 * Nacheinander, weil sich sonst Teilen-Dialog und Mail-App gegenseitig
 * verdecken. Ohne `dateiUri` (Cache-Schreiben scheiterte) nur mailto.
 *
 * Wirft nie; ein Fehler beim Teilen wird sichtbar gemeldet.
 */
export async function mailEntwurfMitDatei(
  mail: MailEntwurf,
  dateiUri: string | null,
  dateiname: string,
  mailtoOeffnen: () => void,
): Promise<MailUebergabe> {
  if (dateiUri && hatPlugin('AppFlags')) {
    try {
      await appFlags().emailEntwurf({ ...mail, dateiUri });
      return 'entwurf';
    } catch (e) {
      if (istNutzerAbbruch(e)) return 'abgebrochen';
      console.warn('[dateiAusgabe] AppFlags.emailEntwurf nicht verfügbar — Rückfall Teilen + mailto:', e);
    }
  }
  if (dateiUri) {
    try {
      await dateiTeilen(dateiUri, dateiname);
    } catch (e) {
      ausgabeFehlerMelden(e);
    }
  }
  mailtoOeffnen();
  return 'rueckfall';
}

const MELDUNG_ID = 'datei-ausgabe-meldung';

/**
 * Sichtbare Fehlermeldung für den App-Zweig, OHNE Browser-Dialog.
 *
 * Warum kein Browser-Dialog (alert/confirm/prompt): die CI-Ratsche
 * (scripts/ci-ratchet.ts) zählt jeden Aufruf in src und wird rot, sobald es
 * mehr werden.
 * Statt dessen ein kurzer Hinweis am unteren Rand (Tippen schließt, nach
 * 8 s verschwindet er selbst). Reines DOM, kein React — damit ihn auch
 * Dienste ohne Oberfläche (emlBuilder, projectReferenceImage) nutzen können.
 */
export function ausgabeFehlerMelden(fehler: unknown): void {
  console.error('[dateiAusgabe] Datei-Ausgabe fehlgeschlagen:', fehler);
  if (typeof document === 'undefined' || !document.body) return;
  const detail = fehlertext(fehler);
  const text = `Datei konnte nicht gespeichert oder geteilt werden.${detail ? ` (${detail})` : ''}`;

  document.getElementById(MELDUNG_ID)?.remove();
  const el = document.createElement('div');
  el.id = MELDUNG_ID;
  el.setAttribute('role', 'alert');
  el.textContent = text;
  Object.assign(el.style, {
    position: 'fixed',
    left: '16px',
    right: '16px',
    bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    zIndex: '2147483000',
    background: '#991b1b',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
    font: '600 14px/1.4 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
  });
  const weg = () => el.remove();
  el.addEventListener('click', weg);
  document.body.appendChild(el);
  setTimeout(weg, 8000);
}

// ─── intern ──────────────────────────────────────────────────────────────────

/** Browser-Download wie file-saver: unsichtbarer Link, Objekt-URL erst nach 40 s frei. */
function webDownload(daten: Blob, dateiname: string): void {
  const url = URL.createObjectURL(daten);
  const a = document.createElement('a');
  a.href = url;
  a.download = dateiname;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Manche Browser lesen den Blob erst nach dem Klick — zu frühes Freigeben
  // ergibt eine leere oder abgebrochene Datei.
  setTimeout(() => URL.revokeObjectURL(url), 40_000);
}

/**
 * Dateiname für den App-Cache. Pfadzeichen und Steuerzeichen raus; dazu
 * `! ~ ' *`: die bleiben in der file://-URI unkodiert, und Androids
 * `MimeTypeMap.getFileExtensionFromUrl` erkennt dann die Endung nicht mehr —
 * das Share-Plugin schickte die Datei mit unbestimmtem Typ statt
 * `application/pdf`, und PDF-Apps fehlten im Teilen-Dialog. Umlaute und Leerzeichen bleiben
 * (werden %-kodiert, das stört die Erkennung nicht).
 */
function cacheDateiname(dateiname: string): string {
  const ohneSteuerzeichen = Array.from(String(dateiname ?? ''))
    .filter((z) => { const c = z.charCodeAt(0); return c >= 32 && c !== 127; })
    .join('');
  let name = ohneSteuerzeichen
    .normalize('NFC')
    .replace(/[/\\:*?"<>|!~']/g, '_')
    .trim()
    .replace(/^[.\s]+/, '');
  if (name.length > 150) {
    const punkt = name.lastIndexOf('.');
    const endung = punkt > 0 && name.length - punkt <= 10 ? name.slice(punkt) : '';
    name = name.slice(0, 150 - endung.length) + endung;
  }
  return name || 'datei';
}

/** Blob → Base64 ohne `data:`-Präfix (Filesystem erwartet reines Base64 für Binärdaten). */
async function blobZuBase64(daten: Blob): Promise<string> {
  if (typeof FileReader !== 'undefined') {
    // Nativ im Browser kodiert — schneller und sparsamer als eine JS-Schleife.
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('Datei konnte nicht gelesen werden.'));
      reader.readAsDataURL(daten);
    });
    const komma = dataUrl.indexOf(',');
    return komma >= 0 ? dataUrl.slice(komma + 1) : dataUrl;
  }
  const bytes = new Uint8Array(await daten.arrayBuffer());
  let binaer = '';
  const STUECK = 0x8000;
  for (let i = 0; i < bytes.length; i += STUECK) {
    binaer += String.fromCharCode(...bytes.subarray(i, i + STUECK));
  }
  return btoa(binaer);
}

/** Nutzer hat den Teilen-Dialog geschlossen (Android: „Share canceled"; Web Share: AbortError). */
function istNutzerAbbruch(e: unknown): boolean {
  if (typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError') return true;
  return /cancel/i.test(fehlertext(e));
}

function fehlertext(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && typeof (e as { message?: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return e === undefined || e === null ? '' : String(e);
}
